import * as React from 'react';
import { Button } from '../Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter } from '../Dialog';
import { Icon } from '../Icon';
import type { FileItemData } from './FileItem';

type FileDeleteDialogProps = {
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * Callback when the dialog should be closed
   */
  onOpenChange: (open: boolean) => void;
  /**
   * The file to be deleted
   */
  file: FileItemData | null;
  /**
   * Callback when delete is confirmed
   */
  onConfirm: (file: FileItemData) => void;
  /**
   * Custom title text
   * @default "Delete file?"
   */
  title?: string;
  /**
   * Custom description text
   * @default "Are you sure you want to delete this file? This action cannot be undone."
   */
  description?: string;
  /**
   * Custom confirm button text
   * @default "Delete"
   */
  confirmText?: string;
  /**
   * Custom cancel button text
   * @default "Cancel"
   */
  cancelText?: string;
};

/**
 * FileDeleteDialog provides a confirmation dialog for file deletion,
 * following the Figma design specifications.
 */
function FileDeleteDialog({
  open,
  onOpenChange,
  file,
  onConfirm,
  title = 'Delete file?',
  description = 'Are you sure you want to delete this file? This action cannot be undone.',
  confirmText = 'Delete',
  cancelText = 'Cancel',
}: FileDeleteDialogProps) {
  const handleConfirm = React.useCallback(() => {
    if (file) {
      onConfirm(file);
      onOpenChange(false);
    }
  }, [file, onConfirm, onOpenChange]);

  const handleCancel = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  if (!file) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full max-w-md p-4 gap-[18px] z-60"
        overlayClassName="z-60"
        title={title}
        data-testid="file-delete-dialog"
        headerProps={{ className: 'p-0' }}
        titleProps={{ className: 'text-[18px] font-semibold text-primary p-0' }}
      >
        <DialogDescription className="text-xs font-normal text-grey-80 leading-[1.4] p-0">
          {description}
        </DialogDescription>

        <DialogFooter className="flex items-center justify-end gap-2 p-0">
          <Button variant="secondary" size="standard" onClick={handleCancel}>
            {cancelText}
          </Button>
          <Button variant="danger" size="standard" onClick={handleConfirm}>
            <Icon icon="trash" size={14} />
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { FileDeleteDialog };
export type { FileDeleteDialogProps };
